# Vision and Values

<div className="goals-article">

<Section index="01" title="Cost of the Average">

<Lede>

<Quote>
  <P><strong>“So, the cost of the average just went to zero and so did its value.”</strong></P>
</Quote>

— Lena Hall, [*The Signal Layer: What to Build When Anything Can Be Built*,
1:25–1:29](https://www.youtube.com/watch?v=1KOdiGgMtpY&t=85s).

Lena's talk is very good. However, parts of it feel more like a description
of 2028. Here in 2026, I can still spend an impressive amount on getting nowhere.

A couple of months ago, after Gippity added goal mode. I was excited to generate some of my own run-of-the-mill-zero-value-average-slop-code. First, using plan mode, I meticulously detailed an exhaustive plan. Then I had the briliant idea. I toggled goal mode on and prompted:

<Quote>
  <P>Optimize this plan and fill all gaps.</P>
</Quote>

And wouldn't you know it. Six hours later, the agent had exhausted my credits, got stuck in a loop between conflicting tasks, and had left me with a worktree that was beyond reconcilation. It was a mess. The cost was and 50 dollars and the value was zero.

Let's fix Lena's quote. "The cost of the average is reduced and yet the value of the average remains at zero" 

</Lede>

### Signal and Strategy

There is an economic shift happening. Generating code is easier, faster but not necessarily cheaper or more effective.

Can AI lift you above the average? No.
If everyone has access, it is by definition not a differentiator. You need a strategic advantage.

Hall says the work is now in the *signal layer*. That is where human insight, vision, and judgment become a strategic advantage: knowing a problem closely, imagining a better outcome, and choosing the tradeoffs worth making to get there.

Gippity and Claudius do not share your values or your vision. Their priorities are shaped by the guys that claim "AI will destroy humanity".

Researchers tested strategic advice
from leading LLMs. Across thousands of simulations, recommendations kept
converging on fashionable strategies even as the business context changed.
They call it [“strategy trendslop”](https://www.stern.nyu.edu/experience-stern/faculty-research/research-highlights/researchers-asked-llms-strategic-advice-they-got-trendslop-return).
trendslop isn't "wrong" persay, but if everyone has the same strategy, what makes it work better for you? What's your strategic advantage?

A strategy starts with a direction, and choices
under uncertainty. Who are we serving? What will we do for them? What are we
willing to sacrifice?

To answer these you need *vision* and *values*. Vision to see the effect of potential outcomes and values to evaluate and measure those effects. 

### Fluency Feels 'Right'

AI knows everything about your customer. It's easy to have multiple threads running market research, product analysis, technical planning, and it's easy to spend hours doing this. In fact, this is often easier than talking to your customer. 


> **“the end game for all … RLHF models is optimizing for engagement”**

— Diogo Almeida, ChatGPT and [InstructGPT coauthor](https://arxiv.org/abs/2203.02155),
[*What's next after RLHF?*, 7:38–7:56](https://ai.engineer/talks/cJ0EOzey--o-jev-ceo-made-chatgpt-building-whats-next).

His point: these assistants are designed to make you "feel good". They are useful and at the same time optimized to maximize engagement.

Almeida argues that optimizing for human approval can work against dependable
automation. A useful agent sometimes needs to stop, expose uncertainty, or tell
me my plan is bad. To me, and I'm not a Gippity cocreator, but seems like the same logic applies to other problem spaces besides automation.

Furthermore, we bring our own baggage along when crafting solutions with AI.

Our feeble human psychologies are fraught with fallacy and bias. Gippity and Claudius aren't designed to help guard against that. In fact, incentive structures suggest otherwise.

 Take for example the *personal validation effect*. A [CHI 2026 study](https://www.microsoft.com/en-us/research/publication/personal-validation-effect-in-llms-positive-ai-responses-bias-perceptions-of-validity-reliability-personalization-and-usefulness-of-fictitious-predictions/)
showed that participants will be inclined toward the "favorable" AI response.

<Asset id="experience-but-lacking" />

There is a psychological shift. AI isn't decietful it doesn't have intentions. It is optimized for engagement and to make you feel good, which according to the research affects our objectivity when evaluating answers.

A response can feel meaningful, but feeling meaningful is not the same as making a meaningful difference.

### What Language Carries—and What It Leaves Out

What happens when an AI set's your aim? Besides subverting your values and its sycofantic misdirections, what else it might it lack?

<Asset id="relational-knowing-figure" />

Consider Jon. “Jon earns $72,000” describes his salary. “Jon feels underpaid”
tells us the salary does not fit what he thinks the work should provide. It does
not tell us whether he wants more income, recognition, time, or autonomy.

For this article, <Term definition="How an outcome is experienced as beneficial, harmful, meaningful, or worth protecting by a person or community.">human value</Term> means how an outcome is experienced as
beneficial, harmful, meaningful, or worth protecting. A
<Term definition="Language expressing part of how an outcome matters to a person or community.">value statement</Term> expresses part of that relationship in language. An
<Term definition="A ranking enacted in behavior or policy, whether or not anyone stated or authorized it.">operative priority</Term> is a ranking enacted in behavior or policy, whether or not anyone stated or authorized it.

A model can recite principles and behave according to learned or instructed
priorities. That does not establish that it experiences those values or has
authority to impose them. Human values concern what outcomes mean in people's
lives. Operative priorities concern what a system rewards, protects, or
sacrifices.

We do not need to upload Jon's entire inner life before helping him. We do need
to ask what “better” means before building him the wrong thing. Share enough to
form a responsible hypothesis, make the tradeoffs visible, and notice when
reality disagrees.

Jon's “I want something better” branches toward legitimate futures:

<Asset id="value-ladder" />

Once those meanings are on the table, we can ask the harder question: which
future should govern the work?

</Section>

<Section index="02" title="Vision Gives Direction. Authority Makes It Governing.">

>**What you aim at determines what you see.**

A <Term definition="A shared, revisable picture of a desirable future and the human consequences that make it worth pursuing.">vision</Term> is a picture of a future worth working toward, shared with others
and open to revision. It gives us direction before we have every goal and plan
worked out. “To the moon” is enthusiastic. It still leaves us asking who benefits,
what it costs, and who agreed to the trip.

An inspiring vision does not automatically give its author the right to impose
it. Four questions keep that authority attached to the people it affects.

<Callout label="Governance test" title="Four requirements">
  <ol>
    <li><strong><Term definition="Whose interests must be considered and who has a route to contest the goal.">Standing</Term>:</strong> whose lives does this affect, and how can they challenge it? Being affected deserves consideration and a way to object, even when it does not grant an automatic veto.</li>
    <li><strong><Term definition="Who may bind an organization to a goal and where that authority stops.">Authorization</Term>:</strong> who actually gets to make this commitment, and where does their authority stop?</li>
    <li><strong><Term definition="Who remains answerable for the benefits, harms, and costs produced by pursuing the goal.">Accountability</Term>:</strong> who answers for the benefits, harms, and costs? Authorizing or executing the goal carries responsibility for its consequences.</li>
    <li><strong><Term definition="The requirement that a goal remain open to revision when evidence conflicts with its stated purpose.">Corrigibility</Term>:</strong> what would make us change our minds? The goal must remain revisable when the consequences contradict its purpose.</li>
  </ol>
</Callout>

Suppose our company offers a book-publishing service. We might help Jon turn his
teaching materials into a book, reach more educators, and earn additional
income without taking on the full work of publishing. That is a promising
offer, not an authorized goal. We still need to learn whether Jon wants it,
whether family time is a governing constraint, whether we can deliver it, and
whether the benefit justifies the cost.

A goal names a condition worth creating or preserving. Once the root goal is
supplied, it becomes valuable to explore opportunities, compare solutions, and
test whether an intervention produces the intended consequence. Strategy
coordinates those commitments over time.

<Asset id="goal-tree-figure" />

Even better reasoning cannot choose the destination for us. Judea Pearl's ladder of
causation distinguishes association, intervention, and counterfactual questions.
AI can help at every rung when the evidence and causal model are available. The
ladder answers increasingly powerful causal questions; it does not add a fourth
rung called “what should we value?”

<Asset id="strategy-map-figure" />

A model may infer a goal from behavior or propose one from public patterns.
Fluency, prediction, and causal competence can support a decision. They do not
create standing to decide whose interests matter.

This is a normative argument, not a laboratory result. Evidence can tell us
what happened. It cannot decide, without a governing judgment, whose experience
should count or which sacrifice is legitimate.

### A System Can Be Coherent and Still Be Wrong

Now for the irritating part: we can state our priorities clearly and still get
them wrong. Every deployed AI system sits inside a hierarchy shaped by training data,
post-training, system instructions, organizational policy, tools, permissions,
and evaluation. People and institutions choose how the surrounding product
orders and enforces those influences.

Someone chooses what counts as a good response, whose feedback matters, and
how disagreements get resolved. “People preferred it” tells us something. It
does not settle whether the result is true, valuable, authorized, or something
we are willing to answer for.

A system can please evaluators, pass its tests, or obey its instructions while
optimizing the wrong target. Our publishing service could celebrate books,
pages, and revenue while Jon earns little after fees and loses more evenings to
work. The dashboard would report success while the value proposition failed.

<Callout label="Failure mode" title="False evaluative closure">
  <P>The system has precise criteria for calling an action better, but those criteria omit or misrank a consequence that should change the judgment. Tests pass because the tests embody the wrong priority. The dashboard stays green because it excludes the person bearing the cost.</P>
</Callout>

AI may identify that contradiction, but it cannot overrule the governing system
unless people have given it permission to challenge, escalate, or stop.

<Asset id="governing-loop-figure" />

We need a way back. Jon must be able to say the offer is making his life worse,
and someone must have the authority to change it. Observe the consequences,
include the people paying the cost, protect disagreement, and make sure an
escalation can actually change the goal, metric, or boundary. Otherwise we have
built a very efficient way to keep being wrong.

Human governance is no guarantee of wisdom. People choose bad goals, protect
status, and rationalize harm. The reason to preserve human responsibility is
not that people are always better than models. Organizations act through social
authority, and those affected need somewhere to direct consent, challenge,
blame, repair, and revision.

### Shared Intent Lets Teams Think for Themselves

None of this means the boss needs to approve every move. That would turn human
judgment into another queue. A strategy that only one person can interpret is
not much use to the rest of the organization.

Suppose product learns that Jon accepts a longer timeline if it protects his
evenings, while operations finds white-glove support unsustainable. Given only
“grow book revenue,” one team may overpromise while the other removes the help
that made the offer valuable.

Shared intent gives them a basis for different but compatible choices. The
vision is not simply “publish more books.” It is to help educators earn from
existing expertise without creating a second full-time job. Teams need the
desired future, affected people, constraints, decision rights, visible
commitments, evidence that should force reconsideration, and protected dissent.

Write that down, and teams have something they can use and challenge. Why does
this action make sense? Whose needs have we missed? What outcome would show
that the offer failed? A longer plan cannot answer those questions by itself.

Inside those boundaries, AI can research, compare explanations, generate
options, and make bounded instrumental decisions. Removing a person from each
action does not remove human responsibility for the objective, permissions,
evaluation, escalation, or consequences.

Teams can choose different routes when they understand the destination, their
boundaries, and how to change course. Shared intent gives them room to think.
It should not become a polite name for obedience.

</Section>

<Section index="03" title="Conclusion — Retain Authority Over the Ends">

Six hours, all my credits, and a discarded worktree did not prove that AI cannot
help with strategy. What I had left undefined was what deserved the time, what
was good enough, and what should be left alone. I handed over those tradeoffs
with the task and called it “optimization.”

Unstated values do not disappear. A system imports priorities from its training,
instructions, tools, evaluation, and request. Stated priorities can still be
wrong. Coherence does not close the values question.

The work is to name the future we want and why it matters, make clear who can
commit us to it, and give teams enough shared intent to act. Then listen when
the evidence—or the people living with the consequences—says we got it wrong.

<Callout label="Responsibility" emphasis>
  <P><strong>The point is not to preserve a ceremonial human approval at the top of an automated system. It is to preserve a chain of responsibility from values to goals, from goals to action, and from action back to consequences.</strong></P>
</Callout>

I still want the machinery. I would also like to keep my credits next time.
But the advantage has to come from understanding a problem, choosing a future
worth pursuing, and staying responsible for what happens when we pursue it.
“Optimize this plan” cannot do that work for us.

This article sets the AI Factory's direction.

<Asset id="article-bundle-graph" />

<ArticleLink slug="understanding-and-bottlenecks">Understanding and Bottlenecks</ArticleLink> asks how to organize work when generation outruns our capacity to evaluate and understand it.

</Section>

<Section index="04" title="Sources" collapsible icon="text">

The argument above is my synthesis. These sources support its strategic opening,
bounded examples, causal distinctions, and account of revisable valuation. They
do not independently prove the normative conclusion.

1. Lena Hall. [*The Signal Layer: What to Build When Anything Can Be Built*](https://ai.engineer/talks/1KOdiGgMtpY-signal-layer-what-build-when-anything-be). AI Engineer World's Fair (2026), especially 1:25–1:29 and 3:21–4:02. The opening quotation describes lost differentiation; later in the talk Hall acknowledges production and attention costs. The signal layer connects a product's distinctive purpose with what customers understand.
2. Angelo Romasanta, Llewellyn D. W. Thomas, and Natalia Levina. [“Researchers Asked LLMs for Strategic Advice. They Got ‘Trendslop’ in Return.”](https://hbr.org/2026/03/researchers-asked-llms-for-strategic-advice-they-got-trendslop-in-return) (2026). [Levina's NYU Stern research summary](https://www.stern.nyu.edu/experience-stern/faculty-research/research-highlights/researchers-asked-llms-strategic-advice-they-got-trendslop-return) describes recurring fashionable recommendations across tested contexts. The result is bounded by the study's models and situations.
3. Bertram R. Forer. [“The Fallacy of Personal Validation: A Classroom Demonstration of Gullibility.”](https://doi.org/10.1037/h0059240) *Journal of Abnormal and Social Psychology* (1949). Supports the classic personal-validation effect used here as an analogy, not a claim about AI deception.
4. Pat Pataranutaporn, Eunhae Lee, Judith Amores, and Pattie Maes. [“Personal Validation Effect in LLMs.”](https://www.microsoft.com/en-us/research/publication/personal-validation-effect-in-llms-positive-ai-responses-bias-perceptions-of-validity-reliability-personalization-and-usefulness-of-fictitious-predictions/) CHI (2026). Supports the bounded finding that positive fictitious AI predictions received higher perceived validity, personalization, reliability, and usefulness; it does not establish that every chatbot interaction has this effect.
5. NIH Expert Consensus Meeting authors. [“Postural Orthostatic Tachycardia Syndrome (POTS): State of the Science and Clinical Care — Part 1.”](https://pmc.ncbi.nlm.nih.gov/articles/PMC8455420/) (2021). Supports the diagnostic criteria and description of POTS as a heterogeneous multisystem syndrome; the article's example distinguishes diagnosis from one complete causal explanation and is not medical advice.
6. Judea Pearl. [“What Is Causal Inference?”](https://bayes.cs.ucla.edu/ijcai-july2022-bw.pdf) IJCAI (2022). Supports the ladder of association, intervention, and counterfactual reasoning; it does not select which outcome ought to govern action.
7. John Dewey. [*Theory of Valuation*](https://archive.org/details/theoryofvaluatio032168mbp) (1939). Supports a consequences-sensitive and revisable account of valuation.
8. Diogo Almeida. [*What's next after RLHF?*](https://ai.engineer/talks/cJ0EOzey--o-whats-next-after-rlhf). AI Engineer World's Fair (2026), especially 5:57–8:22. Supports Almeida's distinction between preference-optimized assistance and calibrated automation, including his engagement argument; the talk does not establish a universal objective shared by every model or prove the reliability of a later product.

</Section>

</div>
